/**
 * Rechtsform aus dem Firmennamen ableiten (`Contact.lastname` bei `typeId = 1`).
 *
 * Ersetzt `matchLegalFormInName` (apps/web), das gegen die Katalog-**Labels**
 * matchte und dabei den **längsten** Treffer gewinnen ließ. Gegen die gepflegten
 * PROD-Firmen gemessen (1.115 Firmen mit Rechtsform ≠ Unbekannt/Sonstige):
 * 44,0 % richtig bei **7,4 % Falschzuordnungen** — „GmbH&Co.KG" ohne Leerzeichen
 * fiel auf „GmbH" durch, und 22 der 36 Katalog-Labels („Aktiengesellschaft (AG)")
 * kommen in keinem Firmennamen vor.
 *
 * Der Hebel ist nicht die Musterliste, sondern die **Position im Namen**: Die
 * Rechtsform steht am Ende (Suffix) oder — bei Objektgemeinschaften — am Anfang
 * (Präfix). Dieselbe Ground Truth: 50,2 % richtig bei 2,1 % Falschzuordnungen.
 *
 * Hier liegt die **eine** Quelle der Wahrheit: Backfill (Migration/Service) und
 * Tipphilfe (Anlagemaske) benutzen physisch dieselbe Tabelle. Die Muster stehen
 * bewusst in Postgres-ARE-Syntax (`\y`, `\M`) und werden für JS über
 * `zuJsRegex()` übersetzt — so ist die Tabelle 1:1 in eine Backfill-Migration
 * übernehmbar, ohne zweite, driftende Musterliste.
 *
 * **Keine Katalog-IDs**: DEV und PROD vergeben für dieselbe Rechtsform
 * verschiedene IDs (PROD 8 = „GmbH & Co. KG", DEV 8 = „AG"). Jede Stufe liefert
 * einen stabilen `key`; `findeKatalogEintrag()` löst ihn über Kürzel/Namen auf
 * und macht bei fehlendem Eintrag **no-op**.
 */
/**
 * Endanker der SUFFIX-Stufen. Toleriert Satzzeichen und **einen** nachgestellten
 * Klammerzusatz („… AG (Deutschland)", „… mbH (GfGR)") — 9 Fälle in PROD.
 */
export const SUFFIX_ANKER = String.raw `([ .,;:/-]|\([^)]{0,40}\))*$`;
/** Länge, bei der der Alt-Import den Firmennamen gekappt hat. */
export const TRUNKIERUNGSLAENGE = 30;
/**
 * Postgres-ARE → JS-RegExp. `\y`/`\M` (Wortanfang/-ende) gibt es in JS nicht,
 * `\b` ist an beiden Stellen äquivalent.
 */
export const zuJsRegex = (muster) => muster.replace(/\\y/g, '\\b').replace(/\\M/g, '\\b');
/**
 * Die Kaskade. Reihenfolge zwingend nach `prio`, **spezifisch vor generisch**:
 * GmbH & Co. KGaA vor GmbH & Co. KG vor KGaA vor GmbH vor KG. Ohne diese
 * Reihenfolge endet „Muster GmbH & Co. KG" als GmbH.
 *
 * Trennertoleranz durchgehend `[ .]*` zwischen den Tokens und `(&|\+|u|und)?`
 * als Bindeglied — deckt „GmbH & Co. KG", „GmbH&Co.KG", „GmbH u. Co. KG" und
 * „GmbH & Co KG". Das führende `g?` in `g?mbh` fängt „Betriebsgesellschaft mbH
 * & Co. KG".
 *
 * ⚠️ Falle (in ARE wie in JS): `\M`/`\b` darf **nicht** hinter einem escapten
 * Punkt stehen. `\ye\.[ ]*v\.\M` matcht nie, weil die Position hinter „." nicht
 * von einem Wortzeichen praezediert wird. Richtig ist `\ye\.[ ]*v\M`.
 */
export const RECHTSFORM_REGELN = [
    // ── Trunkierung: laufen NUR bei exakt 30 Zeichen, nie automatisch ──────────
    { stufe: 'T01', prio: 1, key: 'GMBH_CO_KG', anker: 'TRUNKIERT', konfidenz: 'KEINE', muster: String.raw `g?mbh[ .]*(&|\+|u|und)[ .]*co[ .]*k?\.?$` },
    { stufe: 'T02', prio: 2, key: 'UG_CO_KG', anker: 'TRUNKIERT', konfidenz: 'KEINE', muster: String.raw `ug[ .]*(&|\+|u|und)[ .]*co[ .]*k?\.?$` },
    { stufe: 'T03', prio: 3, key: 'KG', anker: 'TRUNKIERT', konfidenz: 'KEINE', muster: String.raw `(ltd|ag|se|e\.?[ ]?k)\.?[ .]*(&|\+|u|und)[ .]*co[ .]*k?\.?$` },
    { stufe: 'T04', prio: 4, key: 'GMBH', anker: 'TRUNKIERT', konfidenz: 'KEINE', muster: String.raw `\yg[ ]?m[ ]?b?$` },
    { stufe: 'T05', prio: 5, key: 'VVAG', anker: 'TRUNKIERT', konfidenz: 'KEINE', muster: String.raw `\yv{1,2}ag?$` },
    // „…e" am Ende: e.V. oder e.K. — die Unterscheidung ist Handarbeit.
    { stufe: 'T06', prio: 6, key: 'UNKLAR', anker: 'TRUNKIERT', konfidenz: 'KEINE', muster: String.raw `\ye$` },
    // ── Suffix: Rechtsform am Namensende ──────────────────────────────────────
    { stufe: 'S03', prio: 110, key: 'KGAA', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(g?mbh|ug|ag|se)[ .]*(&|\+|u|und)?[ .]*co[ .]*kgaa\M` },
    { stufe: 'S04', prio: 120, key: 'GMBH_CO_KG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `g?mbh[ .]*(&|\+|u|und)?[ .]*co[ .]*kg\M` },
    { stufe: 'S05', prio: 121, key: 'UG_CO_KG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `ug[ .]*(\(haftungsbeschr[^)]*\))?[ .]*(&|\+|u|und)?[ .]*co[ .]*kg\M` },
    // Komplementär ist keine GmbH (AG & Co. KG, Stiftung & Co. KG): die KG steht
    // fest, die Bauform nicht — der Katalog kennt dafür nur „KG".
    { stufe: 'S06', prio: 122, key: 'KG', anker: 'SUFFIX', konfidenz: 'UNSICHER', muster: String.raw `(ltd|ag|se|stiftung|gbr|ohg|e\.?[ ]?k)\.?[ .]*(&|\+|u|und)[ .]*co[ .]*kg\M` },
    // Firmierung mit eingeschobenem Namen („GmbH & Franz Muster KG").
    { stufe: 'S07', prio: 123, key: 'GMBH_CO_KG', anker: 'SUFFIX', konfidenz: 'UNSICHER', muster: String.raw `g?mbh[ .]*(&|\+|u|und)[ .]*(co\.?)?([ ][^ ]{1,24}){0,4}[ ]kg\M` },
    { stufe: 'S08', prio: 130, key: 'KGAA', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ykgaa\M|kommanditgesellschaft auf aktien)` },
    { stufe: 'S09', prio: 140, key: 'GGMBH', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\yggmbh\M|gemeinn[üu]tzige[rn]?[ -]?gmbh)` },
    { stufe: 'S10', prio: 150, key: 'UG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `\yug\M[ ]*\(?[ ]*haftungsbeschr[^)]*\)?` },
    { stufe: 'S11', prio: 151, key: 'UG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `\yug\M` },
    { stufe: 'S12', prio: 160, key: 'GMBH', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ygmbh\M|gesellschaft mit beschr[äa]nkter haftung)` },
    { stufe: 'S13', prio: 161, key: 'GMBH', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `\ymbh\M` },
    { stufe: 'S14', prio: 170, key: 'PARTG_MBB', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ypart[a-z]*[ .]*mbb\M|partnerschaft[a-z]* mit beschr[äa]nkter berufshaftung)` },
    { stufe: 'S15', prio: 171, key: 'PARTG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ypartg\w*\M|partnerschaftsgesellschaft|\ypartnerschaft\M)` },
    { stufe: 'S16', prio: 180, key: 'EK', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ye\.[ ]*k(fm|fr)?\M|eingetragene[rn]? kaufmann|eingetragene kauffrau)` },
    { stufe: 'S17', prio: 190, key: 'EV', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ye\.[ ]*v\M|\yev\M|eingetragener verein)` },
    { stufe: 'S18', prio: 200, key: 'EGBR', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `\yegbr\M` },
    { stufe: 'S19', prio: 201, key: 'GBR', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ygbr\M|gesellschaft b[üu]rgerlichen rechts)` },
    { stufe: 'S20', prio: 210, key: 'OHG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\yohg\M|offene handelsgesellschaft)` },
    { stufe: 'S21', prio: 220, key: 'KG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\ykg\M|\ykommanditgesellschaft\M)` },
    // Der erzwungene Punkt trennt „uniVersa … a.G." von „Muster AG"; Priorität 230
    // vor 250 sichert das zusätzlich ab. Gedeckelt, weil VVaG bis zur
    // Katalog-Konsolidierung kein Ziel hatte.
    { stufe: 'S22', prio: 230, key: 'VVAG', anker: 'SUFFIX', konfidenz: 'UNSICHER', muster: String.raw `(\yvvag\M|versicherungsverein|\ya\.[ ]?g\M)` },
    // SE dualistisch/monistisch ist aus dem Namen prinzipiell nicht ableitbar.
    { stufe: 'S23', prio: 240, key: 'SE', anker: 'SUFFIX', konfidenz: 'UNSICHER', muster: String.raw `(\yse\M|societas europaea)` },
    { stufe: 'S24', prio: 250, key: 'AG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\yag\M|aktiengesellschaft)` },
    { stufe: 'S25', prio: 260, key: 'EG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\yeg\M|\ye\.[ ]?g\M|eingetragene genossenschaft|\ygenossenschaft\M)` },
    { stufe: 'S26', prio: 270, key: 'STIFTUNG', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `\ystiftung` },
    { stufe: 'S27', prio: 280, key: 'AUSLAND', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\y(ltd|limited|plc|inc|llc|llp|sarl|bv|nv|spa|aps|oyj?)\M|\y(s\.[ ]?a\.[ ]?r\.[ ]?l|b\.[ ]?v|n\.[ ]?v|s\.[ ]?a|a/s)\M)` },
    { stufe: 'S28', prio: 290, key: 'AOER', anker: 'SUFFIX', konfidenz: 'SICHER', muster: String.raw `(\y(a[öo]r|kd[öo]r)\M|anstalt des [öo]ffentlichen rechts|k[öo]rperschaft des [öo]ffentlichen rechts)` },
    // ── Präfix: Objektgemeinschaften tragen den Marker VORNE ───────────────────
    // Sie können den Endanker konstruktionsbedingt nie erfüllen („WEG Hauptstr. 5").
    // Suffix läuft davor, deshalb wird „ETG Lehnen-Lehnen GbR" korrekt zur GbR.
    { stufe: 'S01', prio: 305, key: 'GDWE', anker: 'PRAEFIX', konfidenz: 'SICHER', muster: String.raw `(^(weg|gdwe)\M|wohnungseigent[üu]mergemeinschaft)` },
    { stufe: 'S02', prio: 306, key: 'ETG', anker: 'PRAEFIX', konfidenz: 'SICHER', muster: String.raw `(^etg\M|\yeigent[üu]mergemeinschaft)` },
    // Erben-/Grundstücksgemeinschaft ist keine ETG, kommt ihr im Katalog aber am
    // nächsten — deshalb gedeckelt.
    { stufe: 'S29', prio: 307, key: 'ETG', anker: 'PRAEFIX', konfidenz: 'UNSICHER', muster: String.raw `^(erbengemeinschaft|erbgem|grundst[üu]cksgemeinschaft)` },
];
/** Anzeigename je Schlüssel — für Hinweistexte, wenn der Katalog nichts hergibt. */
export const RECHTSFORM_BEZEICHNUNG = {
    GMBH: 'GmbH',
    GGMBH: 'gGmbH',
    UG: 'UG',
    GMBH_CO_KG: 'GmbH & Co. KG',
    UG_CO_KG: 'UG & Co. KG',
    KG: 'KG',
    KGAA: 'KGaA',
    OHG: 'OHG',
    GBR: 'GbR',
    EGBR: 'eGbR',
    AG: 'AG',
    SE: 'SE',
    EG: 'eG',
    VVAG: 'VVaG',
    PARTG: 'PartG',
    PARTG_MBB: 'PartG mbB',
    EK: 'e.K.',
    EV: 'e.V.',
    STIFTUNG: 'Stiftung',
    GDWE: 'GdWE',
    ETG: 'ETG',
    AOER: 'AöR',
    AUSLAND: 'ausländische Rechtsform',
    UNKLAR: 'unklar',
};
/**
 * Katalog-Bezeichnungen je Schlüssel — Kürzel **und** Namen, jeweils in der
 * Schreibweise vor und nach der Konsolidierung, damit die Auflösung auf einer
 * noch nicht migrierten Datenbank ebenso greift.
 *
 * Leere Liste = **bewusst kein Ziel**: `SE` steht im Katalog zweimal
 * (dualistisch/monistisch) und die Unterscheidung ist aus dem Namen nicht
 * ableitbar; `UNKLAR` ist nur eine Prüfmarke.
 */
export const RECHTSFORM_KATALOG_ALIASE = {
    GMBH: ['GmbH', 'Gesellschaft mit beschränkter Haftung (GmbH)'],
    GGMBH: ['gGmbH', 'Gemeinnützige GmbH (gGmbH)'],
    UG: ['UG', 'Unternehmergesellschaft (UG)', 'UG (haftungsbeschränkt)'],
    GMBH_CO_KG: ['GmbH & Co. KG'],
    UG_CO_KG: ['UG & Co. KG', 'UG (haftungsbeschränkt) & Co. KG'],
    KG: ['KG', 'Kommanditgesellschaft (KG)'],
    KGAA: ['KGaA', 'Kommanditgesellschaft auf Aktien (KGaA)'],
    OHG: ['OHG', 'Offene Handelsgesellschaft (OHG)'],
    GBR: ['GbR', 'Gesellschaft bürgerlichen Rechts (GbR)'],
    EGBR: ['eGbR', 'eingetragene GbR (eGbR)'],
    AG: ['AG', 'Aktiengesellschaft (AG)'],
    SE: [],
    EG: ['eG', 'Eingetragene Genossenschaft (eG)', 'Genossenschaft (eG)'],
    VVAG: ['VVaG', 'Versicherungsverein auf Gegenseitigkeit (VVaG)'],
    PARTG: [
        'PartG',
        'Partnerschaftsgesellschaft (PartG)',
        'Partnergesellschaften (PartG)',
        'Partnergesellschaft (PartGG)',
    ],
    PARTG_MBB: [
        'PartG mbB',
        'Partnerschaftsgesellschaft mit beschränkter Berufshaftung (PartG mbB)',
        'Partnergesellschaft mit beschränkter Berufshaftung (PartG mbB)',
        'Partnergesellschaften mit beschränkter Berufshaftung (PartG mbB)',
    ],
    EK: [
        'e.K.',
        'Einzelunternehmer (eingetragener Kaufmann - e.K.)',
        'Eingetragener Kaufmann (e.K.)',
    ],
    EV: ['e.V.', 'Eingetragener Verein (e.V.)'],
    STIFTUNG: ['Stiftung'],
    // „WEG" ist das Gesetz, seit dem WEMoG heißt die Rechtsform GdWE.
    GDWE: ['GdWE', 'Gemeinschaft der Wohnungseigentümer (GdWE/WEG)', 'Wohnungseigentümergemeinschaft (WEG)'],
    ETG: ['ETG', 'Eigentümergemeinschaft (ETG)'],
    AOER: ['AöR/KdöR', 'Anstalt des öffentlichen Rechts (AöR)'],
    // „Limited" ist bewusst NICHT enthalten: der Alt-Eintrag benennt eine einzelne
    // Ausprägung, wo eine Kategorie gebraucht wird (B.V., Inc., S.A. …).
    AUSLAND: ['ausl.', 'Ausländische Rechtsform'],
    UNKLAR: [],
};
const regexCache = new Map();
const kompiliere = (regel) => {
    const cached = regexCache.get(regel.stufe);
    if (cached)
        return cached;
    const quelle = zuJsRegex(regel.muster) + (regel.anker === 'SUFFIX' ? SUFFIX_ANKER : '');
    const re = new RegExp(quelle, 'i');
    regexCache.set(regel.stufe, re);
    return re;
};
const NACH_PRIO = [...RECHTSFORM_REGELN].sort((a, b) => a.prio - b.prio);
const TRUNK_REGELN = NACH_PRIO.filter((r) => r.anker === 'TRUNKIERT');
const NAMENS_REGELN = NACH_PRIO.filter((r) => r.anker !== 'TRUNKIERT');
const OHNE_TREFFER = {
    key: null,
    konfidenz: 'KEINE',
    klasse: 'Z',
    stufe: null,
    anker: null,
    trunkiert: false,
    fundstelle: null,
};
const klasseFuer = (regel, trunkiert) => {
    if (trunkiert)
        return 'C';
    if (regel.konfidenz !== 'SICHER')
        return 'B';
    return regel.anker === 'PRAEFIX' ? 'W' : 'A';
};
/**
 * Rechtsform aus einem Firmennamen ableiten. Liefert immer einen Befund, nie
 * `null` — „nichts gefunden" ist `konfidenz: 'KEINE'`, `klasse: 'Z'`.
 *
 * Bei exakt 30 Zeichen (siehe `RechtsformBefund.trunkiert`) laufen zuerst die
 * Trunkierungsstufen; greift keine, läuft die normale Kaskade, ihr Ergebnis wird
 * aber auf höchstens `UNSICHER` gedeckelt. Die Prüfung ist bewusst `=== 30` und
 * nicht `<= 30`: Namen unter 30 Zeichen sind vollständig, und ein `<`-Guard
 * würde jeden längeren Namen ebenfalls entwerten.
 */
export const erkenneRechtsform = (name) => {
    const roh = (name ?? '').trim();
    if (!roh)
        return OHNE_TREFFER;
    // Verglichen wird mit normalisierten Leerzeichen, gemessen wird der Rohname:
    // die 30 Zeichen sind die Kappungsgrenze des Alt-Imports, kein Suchkriterium.
    // Beides genau wie in der SQL-Fassung der Kaskade (Backfill).
    const wert = roh.replace(/\s+/g, ' ');
    const trunkiert = roh.length === TRUNKIERUNGSLAENGE;
    if (trunkiert) {
        for (const regel of TRUNK_REGELN) {
            const treffer = kompiliere(regel).exec(wert);
            if (!treffer)
                continue;
            return {
                key: regel.key,
                konfidenz: 'KEINE',
                klasse: 'C',
                stufe: regel.stufe,
                anker: 'TRUNKIERT',
                trunkiert: true,
                fundstelle: treffer[0],
            };
        }
    }
    for (const regel of NAMENS_REGELN) {
        const treffer = kompiliere(regel).exec(wert);
        if (!treffer)
            continue;
        return {
            key: regel.key,
            konfidenz: trunkiert && regel.konfidenz === 'SICHER' ? 'UNSICHER' : regel.konfidenz,
            klasse: klasseFuer(regel, trunkiert),
            stufe: regel.stufe,
            anker: regel.anker,
            trunkiert,
            fundstelle: treffer[0],
        };
    }
    // Auch ohne Treffer bleibt ein 30-Zeichen-Name ein Fall für die Prüfliste und
    // nicht für die Handarbeits-Schublade — genau wie in der SQL-Fassung.
    return trunkiert ? { ...OHNE_TREFFER, trunkiert: true, klasse: 'C' } : OHNE_TREFFER;
};
const normalisiere = (wert) => wert.trim().replace(/\s+/g, ' ').toLowerCase();
/**
 * Schlüssel → Katalogeintrag, aufgelöst über Kürzel und Namen, **nie über die
 * ID**. Kein Treffer heißt: nicht schreiben, nur als Vorschlag ausweisen (79
 * VVaG, 15 SE und 38 ausländische Firmen hatten vor der Katalog-Konsolidierung
 * kein Ziel).
 */
export const findeKatalogEintrag = (key, katalog) => {
    if (!key || !katalog?.length)
        return null;
    const aliase = RECHTSFORM_KATALOG_ALIASE[key];
    if (!aliase.length)
        return null;
    for (const alias of aliase) {
        const gesucht = normalisiere(alias);
        const treffer = katalog.find((e) => normalisiere(e.legalForm ?? '') === gesucht ||
            (!!e.kuerzel && normalisiere(e.kuerzel) === gesucht));
        if (treffer)
            return treffer;
    }
    return null;
};
/** Befund und — falls vorhanden — der passende Katalogeintrag in einem Aufruf. */
export const erkenneRechtsformImKatalog = (name, katalog) => {
    const befund = erkenneRechtsform(name);
    return { befund, eintrag: findeKatalogEintrag(befund.key, katalog) };
};
